import { useState, useRef, useCallback } from 'react';
import * as THREE from 'three';
import { Representation } from 'forma-embedded-view-sdk/dist/internal/representations';
import {
	generateFacadeGridPoints,
	extractExteriorWallsFromRooms,
	updateFacadeSunlightVisualization,
	clearFacadeSunlightVisualization,
} from '@/utils/facadeWallPolygons';
import { useDailySunlightStore } from '@/store/dailySunlightsStore';
import { getShadowCastersForFacade, SerializedMesh } from '@/utils/meshSerialization';
import { originalGeometries } from '@/utils/buildingWindows';
import { WorkerOutput } from '@/workers/sunlight.worker';
import { buildDateSequence } from '@/utils/dateSequence';
import { calculateETA } from '@/utils/etaCalculation';

export interface FacadeSunlightHook {
	calculatingFacadeSunlight: boolean;
	calculateFacadeSunlight: (
		buildingObject: THREE.Object3D,
		buildingGraph: Representation.GraphBuilding,
		latitude: number,
		longitude: number
	) => Promise<void>;
	clearFacadeVisualization: () => void;
	startFacadeAnimation: (
		buildingObject: THREE.Object3D,
		buildingGraph: Representation.GraphBuilding,
		latitude: number,
		longitude: number
	) => Promise<void>;
	stopFacadeAnimation: () => void;
	isAnimating: boolean;
	calculateSingleDay: (
		buildingObject: THREE.Object3D,
		buildingGraph: Representation.GraphBuilding,
		latitude: number,
		longitude: number,
		dayIndex: number
	) => Promise<void>;
}

export interface FacadeSunlightParams {
	sceneRef: React.MutableRefObject<THREE.Scene | null>;
}

interface FacadeGridPoint {
	x: number;
	y: number;
	z: number;
	normal: { x: number; y: number; z: number };
}

export const useFacadeSunlight = (params: FacadeSunlightParams): FacadeSunlightHook => {
	const { sceneRef } = params;
	const [calculatingFacadeSunlight, setCalculatingFacadeSunlight] = useState(false);
	const setCurrentStatusMessage = useDailySunlightStore((state) => state.setCurrentStatusMessage);
	const accumulatedFacadeResultsRef = useRef<(number | null)[]>([]);
	const accumulatedFacadePointDetailsRef = useRef<(WorkerOutput['results'][number] | null)[]>([]);
	const animationControlRef = useRef<{ shouldStop: boolean }>({ shouldStop: false });
	const isAnimating = useDailySunlightStore((state) => state.isAnimatingFacade);
	const setIsAnimatingFacade = useDailySunlightStore((state) => state.setIsAnimatingFacade);
	const setCurrentAnimationDay = useDailySunlightStore((state) => state.setCurrentAnimationDay);

	const calculateFacadeSunlight = async (
		buildingObject: THREE.Object3D,
		buildingGraph: Representation.GraphBuilding,
		latitude: number,
		longitude: number
	): Promise<void> => {
		if (!sceneRef.current) {
			console.error('Scene reference is null');
			return;
		}

		setCalculatingFacadeSunlight(true);
		accumulatedFacadeResultsRef.current = [];
		accumulatedFacadePointDetailsRef.current = [];

		try {
			const startDate = useDailySunlightStore.getState().startDate;
			const endDate = useDailySunlightStore.getState().endDate;
			const useApproximation = useDailySunlightStore.getState().useApproximation;

			if (!startDate || !endDate) {
				console.error('Start date or end date is not set');
				setCalculatingFacadeSunlight(false);
				return;
			}

			const analysisDates = buildDateSequence(startDate, endDate, useApproximation);
			const gridSize = useDailySunlightStore.getState().gridSize;

			setCurrentStatusMessage('Extracting exterior walls...');
			const exteriorWalls = extractExteriorWallsFromRooms(buildingObject, buildingGraph);

			setCurrentStatusMessage('Generating facade grid points...');
			const gridPoints = generateFacadeGridPoints(exteriorWalls, gridSize);

			if (gridPoints.length === 0) {
				console.warn('No grid points generated for facade analysis');
				setCalculatingFacadeSunlight(false);
				return;
			}

			const shadowCasters = getShadowCastersForFacade(
				sceneRef.current,
				buildingObject,
				originalGeometries
			);

			// Calculate maxHours dynamically based on analysis period
			const daysAnalyzed = analysisDates.length;
			let maxHours: number;

			if (daysAnalyzed <= 7) {
				maxHours = 8; // Single day or week
			} else if (daysAnalyzed <= 30) {
				maxHours = 10; // Month
			} else {
				maxHours = 12; // Year-long analysis
			}

			const numCores = navigator.hardwareConcurrency || 4;
			const numWorkers = Math.min(Math.max(1, numCores - 1), 8);
			setCurrentStatusMessage(
				`Calculating sunlight for ${gridPoints.length} facade points...`
			);

			await runSunlightWorkers(
				gridPoints,
				shadowCasters,
				latitude,
				longitude,
				analysisDates,
				useApproximation ? 120 : 60,
				numWorkers,
				maxHours,
				gridSize
			);

			setCurrentStatusMessage('');
		} catch (error) {
			console.error('Error calculating facade sunlight:', error);
			setCurrentStatusMessage('');
		} finally {
			setCalculatingFacadeSunlight(false);
		}
	};

	const runSingleWorker = (
		gridPoints: FacadeGridPoint[],
		serializedMeshes: SerializedMesh[],
		latitude: number,
		longitude: number,
		dates: string[],
		timeStepMinutes: number,
		onProgress: (results: WorkerOutput['results']) => void
	): Promise<WorkerOutput['results']> => {
		return new Promise((resolve, reject) => {
			const worker = new Worker(new URL('../workers/sunlight.worker.ts', import.meta.url), {
				type: 'module',
			});

			const timeoutId = setTimeout(() => {
				worker?.terminate();
				reject(new Error('Facade sunlight calculation timed out'));
			}, 10 * 60 * 1000); // 10 minute timeout

			worker.onmessage = (e: MessageEvent) => {
				if (e.data.type === 'progress') {
					const progressBatch = e.data.partialResults || [];
					if (progressBatch.length > 0) {
						onProgress(progressBatch);
					}
					return;
				}

				if (e.data.type === 'complete') {
					clearTimeout(timeoutId);
					const results: WorkerOutput['results'] = e.data.results;
					worker?.terminate();
					resolve(results);
				} else if (e.data.type === 'error') {
					clearTimeout(timeoutId);
					worker?.terminate();
					reject(new Error(e.data.message));
				}
			};

			worker.onerror = (error) => {
				clearTimeout(timeoutId);
				worker?.terminate();
				reject(error);
			};

			worker.postMessage({
				gridPoints: gridPoints.map((p) => ({
					x: p.x,
					y: p.y,
					z: p.z,
				})),
				shadowCasters: serializedMeshes,
				dates: dates,
				latitude,
				longitude,
				timeStepMinutes,
			});
		});
	};

	const runSunlightWorkers = async (
		gridPoints: FacadeGridPoint[],
		shadowCasters: SerializedMesh[],
		latitude: number,
		longitude: number,
		dates: string[],
		timeStepMinutes: number,
		numWorkers: number,
		maxHours: number,
		gridSize: number,
		statusPrefix: string = ''
	): Promise<void> => {
		// Preserve previous results if they exist (for animation continuity)
		const allResults: (number | null)[] = 
			accumulatedFacadeResultsRef.current.length === gridPoints.length
				? [...accumulatedFacadeResultsRef.current]
				: new Array(gridPoints.length).fill(null);
		const allPointDetails: (WorkerOutput['results'][number] | null)[] =
			accumulatedFacadePointDetailsRef.current.length === gridPoints.length
				? [...accumulatedFacadePointDetailsRef.current]
				: new Array(gridPoints.length).fill(null);
		accumulatedFacadeResultsRef.current = allResults;
		accumulatedFacadePointDetailsRef.current = allPointDetails;
		
		const startTime = Date.now();
		let lastUpdateTime = startTime;
		let lastEtaText = '';

		// Interleave grid points across workers for better load balancing
		const workerPromises: Promise<void>[] = [];
		const workerProgress: number[] = new Array(numWorkers).fill(0);
		const workerPointIndices: number[][] = Array.from({ length: numWorkers }, () => []);
		
		// Distribute points in round-robin fashion: worker 0 gets 0,4,8..., worker 1 gets 1,5,9...
		gridPoints.forEach((_, index) => {
			const workerIdx = index % numWorkers;
			workerPointIndices[workerIdx].push(index);
		});

		for (let workerIdx = 0; workerIdx < numWorkers; workerIdx++) {
			const indices = workerPointIndices[workerIdx];
			if (indices.length === 0) continue;
			
			const workerGridPoints = indices.map(idx => gridPoints[idx]);

			const workerPromise = runSingleWorker(
				workerGridPoints,
				shadowCasters,
				latitude,
				longitude,
				dates,
				timeStepMinutes,
				(progressBatch) => {
					// Update accumulated results with progress data
					progressBatch.forEach((result) => {
						const globalIndex = indices[result.pointIndex];
						allResults[globalIndex] = result.averageDailySunlightHours || 0;
						accumulatedFacadeResultsRef.current[globalIndex] = result.averageDailySunlightHours || 0;
						allPointDetails[globalIndex] = result;
						accumulatedFacadePointDetailsRef.current[globalIndex] = result;
					});

					// Update visualization progressively
					if (sceneRef.current) {
						updateFacadeSunlightVisualization(
							sceneRef.current,
							gridPoints,
							accumulatedFacadeResultsRef.current,
							accumulatedFacadePointDetailsRef.current,
							maxHours,
							gridSize
						);
					}

					const localProgress = progressBatch[progressBatch.length - 1].pointIndex + 1;
					workerProgress[workerIdx] = localProgress;

					const totalProcessed = workerProgress.reduce((sum, val) => sum + val, 0);
					const percentage = ((totalProcessed / gridPoints.length) * 100).toFixed(1);
				
					// Calculate ETA
					const etaResult = calculateETA(
						totalProcessed,
						gridPoints.length,
						startTime,
						lastUpdateTime,
						lastEtaText
					);
				
					lastUpdateTime = etaResult.newLastUpdateTime;
					lastEtaText = etaResult.etaText;

					const progressMessage = `Progress: ${totalProcessed}/${gridPoints.length} (${percentage}%)${etaResult.etaText}`;
					setCurrentStatusMessage(
						statusPrefix ? `${statusPrefix} - ${progressMessage}` : progressMessage
					);
				}
			).then((workerResults) => {
				workerResults.forEach((result, idx) => {
					const globalIndex = indices[idx];
					const value = result.averageDailySunlightHours || 0;
					allResults[globalIndex] = value;
					accumulatedFacadeResultsRef.current[globalIndex] = value;
					allPointDetails[globalIndex] = result;
					accumulatedFacadePointDetailsRef.current[globalIndex] = result;
				});
				workerProgress[workerIdx] = workerResults.length;
				
				if (sceneRef.current) {
					updateFacadeSunlightVisualization(
						sceneRef.current,
						gridPoints,
						accumulatedFacadeResultsRef.current,
						accumulatedFacadePointDetailsRef.current,
						maxHours,
						gridSize
					);
				}
			});

			workerPromises.push(workerPromise);
		}

		await Promise.all(workerPromises);
	};

	const clearFacadeVisualization = (): void => {
		if (sceneRef.current) {
			clearFacadeSunlightVisualization(sceneRef.current);
		}
	};

	const startFacadeAnimation = useCallback(async (
		buildingObject: THREE.Object3D,
		buildingGraph: Representation.GraphBuilding,
		latitude: number,
		longitude: number
	): Promise<void> => {
		if (!sceneRef.current || isAnimating) {
			return;
		}

		setIsAnimatingFacade(true);
		animationControlRef.current.shouldStop = false;
		setCalculatingFacadeSunlight(true);

		try {
			const startDate = useDailySunlightStore.getState().startDate;
			const endDate = useDailySunlightStore.getState().endDate;
			if (!startDate || !endDate) {
				console.error('Start date or end date is not set');
				return;
			}

			const gridSize = useDailySunlightStore.getState().gridSize;
			const useApproximation = useDailySunlightStore.getState().useApproximation;

			// Calculate date range
			const analysisDates = buildDateSequence(startDate, endDate, useApproximation);
			const totalDays = analysisDates.length;

			setCurrentStatusMessage('Extracting exterior walls...');
			const exteriorWalls = extractExteriorWallsFromRooms(buildingObject, buildingGraph);

			setCurrentStatusMessage('Generating facade grid points...');
			const gridPoints = generateFacadeGridPoints(exteriorWalls, gridSize);

			if (gridPoints.length === 0) {
				console.warn('No grid points generated for facade analysis');
				return;
			}

			const shadowCasters = getShadowCastersForFacade(
				sceneRef.current,
				buildingObject,
				originalGeometries
			);

			const numCores = navigator.hardwareConcurrency || 4;
			const numWorkers = Math.min(Math.max(1, numCores - 1), 8);
			const maxHours = 8;

			// Start from current day if resuming, otherwise start from 0
			const startDayIndex = useDailySunlightStore.getState().currentAnimationDay || 0;

			// Animate through all analysis dates
			for (let dayIndex = startDayIndex; dayIndex < totalDays; dayIndex++) {
				if (animationControlRef.current.shouldStop) {
					break;
				}

				setCurrentAnimationDay(dayIndex);
				const dateStr = analysisDates[dayIndex];

				// Calculate sunlight for this single day
				await runSunlightWorkers(
					gridPoints,
					shadowCasters,
					latitude,
					longitude,
					[dateStr],
					useApproximation ? 120 : 60,
					numWorkers,
					maxHours,
					gridSize,
					`Animating day ${dayIndex + 1}/${totalDays}: ${new Date(dateStr).toDateString()}`
				);
			}

			// Reset to day 0 if animation completed all days
			if (!animationControlRef.current.shouldStop) {
				setCurrentAnimationDay(0);
			}

			setCurrentStatusMessage('');
		} catch (error) {
			console.error('Error during facade animation:', error);
			setCurrentStatusMessage('');
		} finally {
			setCalculatingFacadeSunlight(false);
			setIsAnimatingFacade(false);
			animationControlRef.current.shouldStop = false;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAnimating, sceneRef, setIsAnimatingFacade, setCurrentAnimationDay, setCurrentStatusMessage]);

	const stopFacadeAnimation = useCallback(() => {
		animationControlRef.current.shouldStop = true;
		setIsAnimatingFacade(false);
	}, [setIsAnimatingFacade]);

	const calculateSingleDay = useCallback(async (
		buildingObject: THREE.Object3D,
		buildingGraph: Representation.GraphBuilding,
		latitude: number,
		longitude: number,
		dayIndex: number
	): Promise<void> => {
		if (!sceneRef.current) {
			console.error('Scene reference is null');
			return;
		}

		setCalculatingFacadeSunlight(true);

		try {
			const startDate = useDailySunlightStore.getState().startDate;
			const endDate = useDailySunlightStore.getState().endDate;
			if (!startDate || !endDate) {
				console.error('Start date or end date is not set');
				return;
			}

			const gridSize = useDailySunlightStore.getState().gridSize;
			const useApproximation = useDailySunlightStore.getState().useApproximation;
			const analysisDates = buildDateSequence(startDate, endDate, useApproximation);

			if (dayIndex < 0 || dayIndex >= analysisDates.length) {
				console.error('Invalid day index');
				return;
			}

			const dateStr = analysisDates[dayIndex];

			setCurrentStatusMessage('Extracting exterior walls...');
			const exteriorWalls = extractExteriorWallsFromRooms(buildingObject, buildingGraph);

			setCurrentStatusMessage('Generating facade grid points...');
			const gridPoints = generateFacadeGridPoints(exteriorWalls, gridSize);

			if (gridPoints.length === 0) {
				console.warn('No grid points generated for facade analysis');
				return;
			}

			const shadowCasters = getShadowCastersForFacade(
				sceneRef.current,
				buildingObject,
				originalGeometries
			);

			const numCores = navigator.hardwareConcurrency || 4;
			const numWorkers = Math.min(Math.max(1, numCores - 1), 8);
			const maxHours = 8;

			setCurrentStatusMessage(`Calculating day ${dayIndex + 1}/${analysisDates.length}: ${dateStr}`);

			await runSunlightWorkers(
				gridPoints,
				shadowCasters,
				latitude,
				longitude,
				[dateStr],
				useApproximation ? 120 : 60,
				numWorkers,
				maxHours,
				gridSize
			);

			setCurrentStatusMessage('');
		} catch (error) {
			console.error('Error calculating single day facade sunlight:', error);
			setCurrentStatusMessage('');
		} finally {
			setCalculatingFacadeSunlight(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sceneRef, setCurrentStatusMessage]);

	return {
		calculatingFacadeSunlight,
		calculateFacadeSunlight,
		clearFacadeVisualization,
		startFacadeAnimation,
		stopFacadeAnimation,
		isAnimating,
		calculateSingleDay,
	};
};
